

// region JS Imports

// region External Modules
import _template from "lodash/template"
// endregion External Modules

// region Private Modules
import dayjs from "plugins/dayjs/index"
import I18n from "plugins/i18n-js"
import NumbersFormatter from "app/numbers/formatter"

import Highcharts from "plugins/highcharts"
// endregion Private Modules

// region Public Components
// endregion Public Components

// region Private Components
import tooltip_point_content_template from "./tooltip_point_content.html._tpl"
import tooltip_header_template from "./tooltip_header.html._tpl"
// endregion Private Components

// endregion JS Imports

const tooltip_header_tmpl = _template(tooltip_header_template)
const tooltip_point_content_tmpl = _template(tooltip_point_content_template)

const create = ($bar_chart_container: JQuery<HTMLElement>, data: any, show_period_comparison: boolean, offset_right: number | null): any => {
  const grouped_transaction_counts = data.grouped_transaction_counts
  const chart_date_label_format_type = data.chart_date_label_format_type

  const grouped_transaction_counts_for_period_comparison = data.grouped_transaction_counts_for_period_comparison

  return Highcharts.chart($bar_chart_container[0] as Highcharts.HTMLDOMElement, {
    chart:    {
      marginLeft:   80,
      marginRight:  offset_right,
      spacingLeft:  0,
      events:      {
        load: function() {
          const chart = this
          const firstSeries = chart.series[0]
          const lastSeries = chart.series[1]

          if (firstSeries && lastSeries && firstSeries.points.length < lastSeries.points.length) {
            const categories = chart.xAxis[0].categories

            for (let i = firstSeries.points.length; i < lastSeries.points.length; i++) {
              const label = categories[i] || "-"
              firstSeries.addPoint({
                y:                 null,
                label:             label,
                value_for_compare: null,
              }, false)
            }
            chart.redraw()
          }
        },
      },
    },
    title:    {
      text: "",
    },
    subtitle: {
      text: "",
    },
    credits:  {
      enabled: false,
    },
    legend:  {
      enabled: false,
    },

    xAxis:   {
      crosshair: {
        width:      1,
        color:      "gray",
        dashStyle:  "shortdot",
        zIndex:     1000000,
      },
      type:       "category",
      categories: grouped_transaction_counts.map(({first_day_of_period}) => {
        const prefix = chart_date_label_format_type === "Q YYYY" ? "Q" : ""

        return [
          prefix,
          dayjs(first_day_of_period).add(0, "month").locale(I18n.locale.toLowerCase()).format(chart_date_label_format_type),
        ].join("")
      }),
      lineColor:  "#F7F7F7",
      labels:     {
        rotation: 0,
        enabled:  true,
        useHTML:  true,
        align:    "center",
        overflow: "visible",
        style:    {
          fontSize:   "12px",
          fontColor:  "#666666",
        },
      },
      tickInterval: Math.ceil(grouped_transaction_counts.length / 12),
    },
    yAxis:   [
      {
        title: {
          text:  "",
        },
        min:       0,
        // https://api.highcharts.com/highcharts/yAxis.max
        // Max value would be automatically calculated if set it as null.
        // Set it as 10 if no data available or max value less than 10
        max:       Math.ceil(Math.max(Math.max(...grouped_transaction_counts.map((e) => e.transaction_count), Math.max(...grouped_transaction_counts_for_period_comparison.map((e) => e.transaction_count)))) / 10 ) * 10,
        lineWidth: 2,
        lineColor: "transparent",
        labels:    {
          // https://api.highcharts.com/highcharts/xAxis.labels.align
          align:        "left",
          reserveSpace: true,
          style:        {
            fontSize:  "13px",
            fontColor: "#1d3960",
          },
        },
        // limit max number of yAxis to avoid unexpected overflow
        tickAmount:    6,
        tickInterval:  Math.ceil(Math.max(Math.max(...grouped_transaction_counts.map((e) => e.transaction_count), Math.max(...grouped_transaction_counts_for_period_comparison.map((e) => e.transaction_count)))) / 10 ) * 10 / 5,
        allowDecimals: false,
        gridLineColor: "#F7F7F7",
        endOnTick:     false,
      },
    ],
    plotOptions: {
      column: {
        pointPadding: 0,
        borderWidth:  0,
        borderRadius: {
          radius: 0,
        },
        /* Here is the setting to limit the maximum column width. */
        maxPointWidth: 41,
        point:         {
          events: {
            mouseOver: function() {
              this.series.chart.series.forEach(series => {
                series.points.forEach(point => {
                  if (point.x === this.x) {
                    point.setState("hover")
                  }
                })
              })
            },
          },
        },
      },
      series: {
        states: {
          inactive: {
            enabled: false,
          },
        },
      },
    },
    tooltip: {
      shared:     true,
      positioner: function(boxWidth, boxHeight, point) {
        // modify from example: https://jsfiddle.net/jugal/bAw5J/
        const chart = this.chart
        const distance = 16

        let x = chart.plotLeft + point.plotX + distance

        if ( (x + boxWidth) > (chart.plotLeft + chart.plotWidth) ) {
          x = point.plotX + chart.plotLeft - boxWidth - distance
        }

        let y
        if (point.y) {
          y = point.plotY - boxHeight / 2.0
        } else {
          y = (chart.plotHeight - boxHeight) / 2.0
        }

        return {
          x: x,
          y: y,
        }
      },
      useHTML:          true,
      backgroundColor:  null,
      borderWidth:      0,
      shadow:           false,
      style:            {
        padding: 0,
      },
      formatter: function() {
        // region header section

        const current_value = grouped_transaction_counts[this.point.index]?.transaction_count
        const prev_value = grouped_transaction_counts_for_period_comparison[this.point.index]?.transaction_count
        const percentage_diff = (current_value - prev_value) / prev_value
        const percentage_text = Number(percentage_diff).toLocaleString(I18n.locale,{style: "percent",minimumFractionDigits: 1, maximumFractionDigits: 1})

        let percentage_label = ""
        let extra_class = ""

        if ( show_period_comparison ) {
          if ( current_value === 0 && prev_value === 0 ) {
            percentage_label = "+0.0%"
            extra_class = "positive-growth"
          }
          else if (percentage_diff >= 0) {
            percentage_label = `+${percentage_text}`
            extra_class = "positive-growth"
          }
          else {
            percentage_label = `${percentage_text}`
            extra_class = "negative-growth"
          }
        }

        const header_element = tooltip_header_tmpl({
          percentage_label:  percentage_label,
          extra_class:       extra_class,
        })
        // endregion header section


        // region point content section

        const point_content_element: string[] = []

        this.series.chart.series.forEach((series) => {
          const point = series.points[this.point.index]

          if (point) {
            point_content_element.push(
              tooltip_point_content_tmpl({
                name:          this.key,
                symbol_color:  point.symbol_color,
                symbol:        point.symbol,
                label:         point.label,
                y:             NumbersFormatter.numberWithCommas(point.y || 0),
              })
            )
          }
        })

        // endregion point content section

        return [
          header_element,
          point_content_element.join(""),
        ].join("")
      },
    },

    series: [
      {
        type:    "column",
        color:   "#CCD2D9",
        data:    grouped_transaction_counts.map((e) => {
          const label = [
            chart_date_label_format_type === "Q YYYY" ? "Q" : "",
            dayjs(e.first_day_of_period).add(0, "month").locale(I18n.locale.toLowerCase()).format(chart_date_label_format_type),
          ].join("")

          return {
            y:            e.transaction_count,
            label:        label,
            symbol_color: "#3A7DC0",
            states:       {
              hover:  {
                color: "#3A7DC0",
              },
            },
          }
        }),
      },
      {
        type:    "column",
        visible: show_period_comparison,
        color:   "#EACAA6",
        data:    grouped_transaction_counts_for_period_comparison.map((e) => {
          const label = [
            chart_date_label_format_type === "Q YYYY" ? "Q" : "",
            dayjs(e.first_day_of_period).add(0, "month").locale(I18n.locale.toLowerCase()).format(chart_date_label_format_type),
          ].join("")

          return {
            y:            e.transaction_count,
            label:        label,
            symbol_color: "#FF8700",
            states:       {
              hover:  {
                color: "#FF8700",
              },
            },
          }
        }),
      },
    ],
  })

}

export default {create}
